import { getFileDownloadUrl } from '@/shared/api/assets.api';
import { useQuery } from '@tanstack/react-query';

const CACHE_NAME = 'asset-images';

/**
 * Fetches an image and caches it in the browser's Cache API.
 * Uses assetId as the cache key, so changing signed URLs don't cause re-downloads.
 * Since assetId content is immutable, cache entries never expire - browser handles
 * eviction via LRU when storage quota is reached.
 */
async function getCachedImage(assetId: string): Promise<string> {
  const cacheKey = new Request(`/cached-assets/${assetId}`);

  const cache = await caches.open(CACHE_NAME);
  const cached = await cache.match(cacheKey);

  if (cached) {
    const blob = await cached.blob();
    return URL.createObjectURL(blob);
  }

  // Not cached - fetch signed URL and then fetch the image
  // A missing image falls back to a placeholder, so a deleted asset needs no toast.
  const signedUrl = await getFileDownloadUrl(assetId, { silentNotFound: true });
  const response = await fetch(signedUrl);

  if (!response.ok) {
    throw new Error(`Failed to fetch image: ${response.status}`);
  }

  // Store in cache with stable key
  await cache.put(cacheKey, response.clone());

  const blob = await response.blob();
  return URL.createObjectURL(blob);
}

/**
 * Hook for fetching and caching images by asset ID.
 * Images are cached in the browser's Cache API, keyed by assetId.
 * This ensures that changing signed URLs don't cause unnecessary re-downloads.
 */
export const useGetDownloadUrl = (assetId?: string) => {
  const query = useQuery({
    queryKey: ['cachedImage', assetId],
    queryFn: () => {
      if (!assetId) {
        throw new Error('Asset ID is required');
      }
      return getCachedImage(assetId);
    },
    enabled: !!assetId,
    staleTime: Infinity,
    gcTime: Infinity,
    retry: false,
  });

  // TODO: remove placeholder
  return {
    downloadUrl: query.data,
    isLoading: query.isLoading,
    error: query.error,
    isError: query.isError,
    isSuccess: query.isSuccess,
    refetch: query.refetch,
  };
};
